"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NetNamingRuleChain = void 0;
const NetGroupManager_1 = require("./NetGroupManager");
const NetNamingDebugInfo_1 = require("./NetNamingDebugInfo");
const NetNamingRules_1 = require("./NetNamingRules");
class NetNamingRuleChain {
    constructor(enableDebug = false) {
        this.enableDebug = enableDebug;
        this.rules = [];
        this.localNamingRules = [];
    }
    add(rule) {
        if (rule.type === NetNamingRules_1.NetNamingRuleType.LocalNaming) {
            this.localNamingRules.push(rule);
        }
        else {
            this.rules.push(rule);
        }
        return this;
    }
    execute(hierarchy) {
        let manager = new NetGroupManager_1.NetGroupManager();
        if (this.enableDebug) {
            this.resetDebugInfo();
            this.takeSnapshot(manager, -1, 'initial', 'initial', hierarchy);
        }
        for (const rule of this.localNamingRules) {
            rule.apply(hierarchy);
        }
        for (let i = 0; i < this.rules.length; i++) {
            const rule = this.rules[i];
            if (rule.type === NetNamingRules_1.NetNamingRuleType.Group) {
                manager = rule.apply(manager, hierarchy);
                if (this.debugInfo) {
                    this.takeSnapshot(manager, i, rule.constructor.name, 'grouping', hierarchy);
                }
            }
            else if (rule.type === NetNamingRules_1.NetNamingRuleType.Naming) {
                for (const group of manager.iterateGroups()) {
                    if (group.physicalName) {
                        continue;
                    }
                    const name = rule.apply(group, hierarchy);
                    if (name) {
                        group.physicalName = name;
                    }
                }
                if (this.debugInfo) {
                    this.takeSnapshot(manager, i, rule.constructor.name, 'naming', hierarchy);
                }
            }
            else if (rule.type === NetNamingRules_1.NetNamingRuleType.Finalize) {
                rule.apply(Array.from(manager.iterateGroups()), hierarchy);
                if (this.debugInfo) {
                    this.takeSnapshot(manager, i, rule.constructor.name, 'finalize', hierarchy);
                }
            }
            else if (rule.type === NetNamingRules_1.NetNamingRuleType.LocalNaming) {
            }
            else {
                throw new Error(`Unknown rule type: ${rule.type}`);
            }
        }
        if (this.debugInfo) {
            this.computeTrackedObjectHistories();
        }
        return Array.from(manager.iterateGroups());
    }
    trackConnection(moduleRef, moduleName, pinRef, instancePathId = '') {
        if (!moduleRef && !moduleName) {
            throw new Error('Must provide either moduleRef or moduleName');
        }
        this.trackConnectionInternal({
            moduleRef,
            moduleName,
            pinRef,
            instancePathId,
        });
        return this;
    }
    trackLabel(labelType, originalLabel, position, instancePathId = '') {
        this.trackLabelInternal({
            labelType,
            originalLabel,
            position,
            instancePathId,
        });
        return this;
    }
    trackAllConnections() {
        return this;
    }
    trackAllLabels() {
        return this;
    }
    getConnectionHistory(moduleRef, moduleName, pinRef, instancePathId) {
        if (!moduleRef && !moduleName) {
            throw new Error('Must provide either moduleRef or moduleName');
        }
        if (!this.debugInfo) {
            return undefined;
        }
        const searchAllInstances = instancePathId === null || instancePathId === undefined;
        const searchInstancePathId = instancePathId ?? '';
        if (moduleRef) {
            if (searchAllInstances) {
                for (const [_key, history] of this.debugInfo.trackedObjects) {
                    if (history.identifier.type === 'connection' &&
                        history.identifier.connection.moduleRef === moduleRef &&
                        history.identifier.connection.pinRef === pinRef) {
                        return history;
                    }
                }
            }
            else {
                const identifier = {
                    type: 'connection',
                    connection: { moduleRef, pinRef, instancePathId: searchInstancePathId },
                };
                const key = (0, NetNamingDebugInfo_1.serializeTrackedObjectIdentifier)(identifier);
                const result = this.debugInfo.trackedObjects.get(key);
                if (result) {
                    return result;
                }
            }
        }
        if (moduleName) {
            for (const [_key, history] of this.debugInfo.trackedObjects) {
                if (history.identifier.type === 'connection' &&
                    history.identifier.connection.moduleName === moduleName &&
                    history.identifier.connection.pinRef === pinRef &&
                    (searchAllInstances || history.identifier.connection.instancePathId === searchInstancePathId)) {
                    return history;
                }
            }
        }
        return undefined;
    }
    getLabelHistory(labelType, originalLabel, position, instancePathId = '') {
        if (!this.debugInfo) {
            return undefined;
        }
        const identifier = {
            type: 'label',
            label: { labelType, originalLabel, position, instancePathId },
        };
        const key = (0, NetNamingDebugInfo_1.serializeTrackedObjectIdentifier)(identifier);
        return this.debugInfo.trackedObjects.get(key);
    }
    getConnectionPath(moduleRef, moduleName, pinRef, instancePathId) {
        const history = this.getConnectionHistory(moduleRef, moduleName, pinRef, instancePathId);
        if (!history) {
            return 'Not tracked';
        }
        const displayName = moduleRef ?? moduleName ?? 'unknown';
        const actualInstancePathId = history.identifier.type === 'connection' ? history.identifier.connection.instancePathId : '';
        let path = `Connection ${displayName}-${pinRef}${actualInstancePathId ? ` (${actualInstancePathId})` : ''}:\n`;
        for (const entry of history.history) {
            path += `  [${entry.ruleIndex}] ${entry.ruleName}: `;
            if (entry.event === 'created') {
                path += `Started in group ${entry.groupId}\n`;
            }
            else if (entry.event === 'stayed') {
                path += `Remained in group ${entry.groupId}\n`;
            }
            else if (entry.event === 'merged-into') {
                path += `Moved from group ${entry.details?.fromGroupId} to group ${entry.groupId}`;
                if (entry.details?.mergedWithGroupIds && entry.details.mergedWithGroupIds.length > 0) {
                    path += ` (merged with groups ${entry.details.mergedWithGroupIds.join(', ')})`;
                }
                path += '\n';
            }
            else if (entry.event === 'named') {
                path += `Named "${entry.details?.assignedName ?? '<unknown>'}" in group ${entry.groupId}\n`;
            }
        }
        path += `  Final: Group ${history.finalGroupId}, Name: ${history.finalName ?? '<unnamed>'}`;
        return path;
    }
    getLabelPath(labelType, originalLabel, position, instancePathId = '') {
        const history = this.getLabelHistory(labelType, originalLabel, position, instancePathId);
        if (!history) {
            return 'Not tracked';
        }
        let path = `Label ${labelType}:"${originalLabel}"${instancePathId ? ` (${instancePathId})` : ''}:\n`;
        for (const entry of history.history) {
            path += `  [${entry.ruleIndex}] ${entry.ruleName}: `;
            if (entry.event === 'created') {
                path += `Started in group ${entry.groupId}\n`;
            }
            else if (entry.event === 'stayed') {
                path += `Remained in group ${entry.groupId}\n`;
            }
            else if (entry.event === 'merged-into') {
                path += `Moved from group ${entry.details?.fromGroupId} to group ${entry.groupId}`;
                if (entry.details?.mergedWithGroupIds && entry.details.mergedWithGroupIds.length > 0) {
                    path += ` (merged with groups ${entry.details.mergedWithGroupIds.join(', ')})`;
                }
                path += '\n';
            }
            else if (entry.event === 'named') {
                path += `Named "${entry.details?.assignedName ?? 'unknown'}" in group ${entry.groupId}\n`;
            }
        }
        path += `  Final: Group ${history.finalGroupId}, Name: ${history.finalName ?? 'unnamed'}`;
        return path;
    }
    exportDebugInfo() {
        return this.debugInfo;
    }
    getOrCreateGroupId(group) {
        if (!this.debugInfo) {
            throw new Error('Debug mode not enabled');
        }
        let groupId = this.debugInfo.groupIdMap.get(group);
        if (groupId === undefined) {
            groupId = this.debugInfo.groupIdCounter++;
            this.debugInfo.groupIdMap.set(group, groupId);
        }
        return groupId;
    }
    trackConnectionInternal(connection) {
        if (!this.debugInfo) {
            return;
        }
        const identifier = {
            type: 'connection',
            connection,
        };
        const key = (0, NetNamingDebugInfo_1.serializeTrackedObjectIdentifier)(identifier);
        if (!this.debugInfo.trackedObjects.has(key)) {
            this.debugInfo.trackedObjects.set(key, {
                identifier,
                history: [],
                finalGroupId: -1,
            });
        }
    }
    trackLabelInternal(label) {
        if (!this.debugInfo) {
            return;
        }
        const identifier = {
            type: 'label',
            label,
        };
        const key = (0, NetNamingDebugInfo_1.serializeTrackedObjectIdentifier)(identifier);
        if (!this.debugInfo.trackedObjects.has(key)) {
            this.debugInfo.trackedObjects.set(key, {
                identifier,
                history: [],
                finalGroupId: -1,
            });
        }
    }
    createGroupSnapshot(group, hierarchy) {
        if (!this.debugInfo) {
            throw new Error('Debug mode not enabled');
        }
        const connections = [];
        const labels = [];
        let netCount = 0;
        for (const [instance, nets] of group.getNets()) {
            netCount += nets.length;
            for (const net of nets) {
                for (const conn of net.connections) {
                    connections.push({
                        moduleRef: conn.connection.moduleRef,
                        moduleName: conn.connection.moduleName,
                        pinRef: conn.connection.pinRef,
                        instancePathId: this.getInstancePathId(instance, hierarchy),
                    });
                }
                for (const label of net.labels) {
                    labels.push({
                        labelType: label.labelType,
                        originalLabel: label.originalLabel,
                        position: label.position,
                        instancePathId: this.getInstancePathId(instance, hierarchy),
                    });
                }
            }
        }
        return {
            groupId: this.getOrCreateGroupId(group),
            netCount,
            physicalName: group.physicalName ? group.physicalName : undefined,
            connections,
            labels,
        };
    }
    getInstancePathId(instance, hierarchy) {
        if (hierarchy.instance === instance) {
            return hierarchy.pathUniqueId;
        }
        for (const child of hierarchy.children) {
            const result = this.getInstancePathId(instance, child);
            if (result !== '') {
                return result;
            }
        }
        return '';
    }
    createManagerSnapshot(manager, ruleIndex, ruleName, ruleType, hierarchy) {
        if (!this.debugInfo) {
            throw new Error('Debug mode not enabled');
        }
        const groups = [];
        for (const group of manager.iterateGroups()) {
            groups.push(this.createGroupSnapshot(group, hierarchy));
        }
        return {
            ruleIndex,
            ruleName,
            ruleType,
            groups,
            timestamp: Date.now(),
        };
    }
    takeSnapshot(manager, ruleIndex, ruleName, ruleType, hierarchy) {
        if (!this.debugInfo) {
            return;
        }
        const snapshot = this.createManagerSnapshot(manager, ruleIndex, ruleName, ruleType, hierarchy);
        this.debugInfo.snapshots.push(snapshot);
    }
    findGroupContaining(snapshot, identifier) {
        for (const group of snapshot.groups) {
            if (identifier.type === 'connection') {
                const conn = identifier.connection;
                const found = group.connections.some((c) => {
                    if (conn.moduleRef && c.moduleRef === conn.moduleRef) {
                        return c.pinRef === conn.pinRef && c.instancePathId === conn.instancePathId;
                    }
                    if (conn.moduleName && c.moduleName === conn.moduleName) {
                        return c.pinRef === conn.pinRef && c.instancePathId === conn.instancePathId;
                    }
                    return false;
                });
                if (found) {
                    return group.groupId;
                }
            }
            else {
                const label = identifier.label;
                const found = group.labels.some((l) => l.labelType === label.labelType &&
                    l.originalLabel === label.originalLabel &&
                    l.instancePathId === label.instancePathId &&
                    l.position[0] === label.position[0] &&
                    l.position[1] === label.position[1]);
                if (found) {
                    return group.groupId;
                }
            }
        }
        return undefined;
    }
    determineEvent(previousGroupId, currentGroupId, snapshot) {
        if (previousGroupId === undefined) {
            return 'created';
        }
        if (previousGroupId === currentGroupId) {
            return snapshot.ruleType === 'naming' ? 'named' : 'stayed';
        }
        return 'merged-into';
    }
    findMergedGroups(previousSnapshot, currentSnapshot, targetGroupId) {
        const mergedGroupIds = [];
        const targetGroup = currentSnapshot.groups.find((g) => g.groupId === targetGroupId);
        if (!targetGroup) {
            return [];
        }
        for (const prevGroup of previousSnapshot.groups) {
            if (prevGroup.groupId === targetGroupId) {
                continue;
            }
            const hasSharedConnection = prevGroup.connections.some((conn) => targetGroup.connections.some((tc) => tc.moduleRef === conn.moduleRef && tc.pinRef === conn.pinRef && tc.instancePathId === conn.instancePathId));
            if (hasSharedConnection) {
                mergedGroupIds.push(prevGroup.groupId);
            }
        }
        return mergedGroupIds;
    }
    computeTrackedObjectHistories() {
        if (!this.debugInfo) {
            return;
        }
        if (this.debugInfo.trackedObjects.size === 0) {
            const firstNonEmptySnapshot = this.debugInfo.snapshots.find((s) => s.groups.length > 0);
            if (firstNonEmptySnapshot) {
                for (const group of firstNonEmptySnapshot.groups) {
                    for (const conn of group.connections) {
                        this.trackConnectionInternal(conn);
                    }
                    for (const label of group.labels) {
                        this.trackLabelInternal(label);
                    }
                }
            }
        }
        for (const [_key, history] of this.debugInfo.trackedObjects) {
            const identifier = history.identifier;
            let previousGroupId;
            for (const snapshot of this.debugInfo.snapshots) {
                const currentGroupId = this.findGroupContaining(snapshot, identifier);
                if (currentGroupId === undefined) {
                    continue;
                }
                const entry = {
                    ruleIndex: snapshot.ruleIndex,
                    ruleName: snapshot.ruleName,
                    ruleType: snapshot.ruleType,
                    groupId: currentGroupId,
                    event: this.determineEvent(previousGroupId, currentGroupId, snapshot),
                };
                if (entry.event === 'merged-into' && previousGroupId !== undefined) {
                    const previousSnapshot = this.debugInfo.snapshots.find((s) => s.ruleIndex === snapshot.ruleIndex - 1);
                    if (previousSnapshot) {
                        entry.details = {
                            fromGroupId: previousGroupId,
                            mergedWithGroupIds: this.findMergedGroups(previousSnapshot, snapshot, currentGroupId),
                        };
                    }
                }
                if (entry.event === 'named') {
                    const currentGroup = snapshot.groups.find((g) => g.groupId === currentGroupId);
                    if (currentGroup) {
                        entry.details = {
                            assignedName: currentGroup.physicalName,
                        };
                    }
                }
                history.history.push(entry);
                previousGroupId = currentGroupId;
            }
            history.finalGroupId = previousGroupId ?? -1;
            if (previousGroupId !== undefined && this.debugInfo.snapshots.length > 0) {
                const lastSnapshot = this.debugInfo.snapshots[this.debugInfo.snapshots.length - 1];
                const finalGroup = lastSnapshot.groups.find((g) => g.groupId === previousGroupId);
                if (finalGroup) {
                    history.finalName = finalGroup.physicalName;
                }
            }
        }
    }
    resetDebugInfo() {
        this.debugInfo = {
            enabled: true,
            snapshots: [],
            trackedObjects: new Map(),
            groupIdMap: new WeakMap(),
            groupIdCounter: 0,
        };
    }
}
exports.NetNamingRuleChain = NetNamingRuleChain;
//# sourceMappingURL=NetNamingRuleChain.js.map